/**
 * GraphQL for the 501c3 determination-letter upload on the Manage Account page.
 *
 * Three-step handoff, mirroring the CSV import in `integrationQueries.ts`:
 *   1. `rescueGet501c3UploadUrl` — ask the backend for a short-lived presigned
 *      PUT URL, then PUT the PDF bytes directly to S3.
 *   2. `rescueSubmit501c3Document` — once the PUT succeeds, hand the object key
 *      off to be filed for review. Returns the new document row (PENDING).
 *   3. `rescueCurrent501c3Document` — read the current document + review status
 *      back on page load.
 *
 * Documents are scoped to the logged-in user, not the org: in a rescue with
 * several portal logins, each user sees only the letters they uploaded.
 */

import { gql } from '@apollo/client';

/**
 * Review state of a submitted document. `SUPERSEDED` exists on the backend
 * enum but never reaches the portal — `rescueCurrent501c3Document` returns the
 * most recent non-superseded row.
 */
export type RescueDocumentStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** Mirrors the `RescueDocument` GQL type (the fields the card renders). */
export interface RescueDocument {
  id: string;
  status: RescueDocumentStatus;
  originalFileName: string;
  sizeBytes: number;
  uploadedAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
  /** Date-only, as the column stores it. Null when the document never expires. */
  expirationDate: string | null;
  neverExpires: boolean;
  expired: boolean;
  /** A freshly signed S3 link, so this is only selected where a View action exists. */
  downloadUrl: string;
}

const rescueDocumentFields = gql`
  fragment Rescue501c3Fields on RescueDocument {
    id
    status
    originalFileName
    sizeBytes
    uploadedAt
    reviewedAt
    rejectionReason
    expirationDate
    neverExpires
    expired
    downloadUrl
  }
`;

/**
 * The caller's current 501c3 document, or null if they've never submitted one.
 *
 * `downloadUrl` signs a fresh S3 URL per document on every read, which is the
 * cost of the card's View action. Nothing else selects this fragment often
 * enough for that to matter.
 */
export const rescueCurrent501c3DocumentQuery = gql`
  query rescueCurrent501c3Document {
    rescueCurrent501c3Document {
      ...Rescue501c3Fields
    }
  }
  ${rescueDocumentFields}
`;

export interface RescueCurrent501c3DocumentData {
  rescueCurrent501c3Document: RescueDocument | null;
}

/**
 * Request a presigned S3 PUT URL for the determination letter. The returned
 * `url` is short-lived; the client must PUT the body with the `Content-Type`
 * the `contentType` enum resolves to (`APPLICATION_PDF` → `application/pdf`) —
 * S3 enforces an exact header match against the signed request. `key` is the
 * resulting object key, passed on to `rescueSubmit501c3Document`.
 */
export const rescueGet501c3UploadUrlQuery = gql`
  query rescueGet501c3UploadUrl($input: RescueGet501c3UploadUrlInput!) {
    rescueGet501c3UploadUrl(input: $input) {
      url
      key
      expiration
    }
  }
`;

/** File format the client will upload. PDF is the only accepted format. */
export type Rescue501c3ContentType = 'APPLICATION_PDF';

export interface RescueGet501c3UploadUrlData {
  rescueGet501c3UploadUrl: {
    url: string;
    key: string;
    expiration: string;
  };
}

/**
 * File a freshly-uploaded letter for review. Pass the `key` returned by
 * `rescueGet501c3UploadUrl` (after the S3 PUT succeeds). Not idempotent —
 * calling twice supersedes the first row and re-queues, which is also how a
 * rescue replaces a REJECTED letter.
 */
export const rescueSubmit501c3DocumentMutation = gql`
  mutation rescueSubmit501c3Document($input: RescueSubmit501c3DocumentInput!) {
    rescueSubmit501c3Document(input: $input) {
      ...Rescue501c3Fields
    }
  }
  ${rescueDocumentFields}
`;

export interface RescueSubmit501c3DocumentVariables {
  input: {
    /** S3 object key returned by `rescueGet501c3UploadUrl`. */
    uploadedFileKey: string;
    /** Original file name, for display in the portal and admin review. */
    fileName: string;
  };
}

export interface RescueSubmit501c3DocumentData {
  rescueSubmit501c3Document: RescueDocument;
}

/* ------------------------------------------------------------------------- *
 * The reworked flow.
 *
 * The queries above answer one question — "has this rescue filed a letter?" —
 * and the rework asks two more: which document each program wants next, and
 * what one specific order needs given where it ships. A rescue may hold a
 * federal letter plus a certificate per jurisdiction, so the shapes below are
 * lists where the old ones were singletons.
 * ------------------------------------------------------------------------- */

/** The two document types that survive the rework. */
export type RescueDocumentType = 'NONPROFIT_LETTER' | 'STATE_EXEMPTION';

/** Programs that can ask a rescue for documents. Gift with Adoption asks for none. */
export type RescueProgram = 'FI_NANO' | 'FI_DEVICES' | 'GIFT_WITH_ADOPTION';

/**
 * Why the portal can't state a requirement. The server sends a kind and a remedy rather than copy,
 * so the wording lives here and the backend needn't change to reword it.
 */
export type RescueExemptionNoticeKind = 'ADDRESS_REQUIRED' | 'JURISDICTION_UNSUPPORTED' | 'MAY_BE_GOVERNMENT_ENTITY';
export type RescueExemptionNoticeRemedy = 'SELF_SERVE_ACCOUNT_PAGE' | 'CONTACT_SUPPORT';

export interface RescueExemptionNotice {
  kind: RescueExemptionNoticeKind;
  remedy: RescueExemptionNoticeRemedy;
}

/** Which document a jurisdiction accepts. Mirrors `RescueStateExemptionBucket`. */
export type RescueStateExemptionBucket = 'MULTISTATE_F0003' | 'STATE_CERTIFICATE' | 'NO_STATE_DOCUMENT';

/** One document the rescue is asked for, paired with whatever it has filed against it. */
export interface RescueRequiredDocument {
  documentType: RescueDocumentType;
  /** The jurisdiction it answers. Null on the letter, which is federal. */
  state: string | null;
  /** The office that issues this state's certificate, when Finance has named one. */
  issuingAgency: string | null;
  /** Which answer the jurisdiction takes, so the uploader can name the form it wants. */
  bucket: RescueStateExemptionBucket | null;
  document: RescueDocument | null;
  /** Approved and unexpired. What a later order needs. */
  satisfied: boolean;
  /** Something is filed whatever its review state. Enough for a first order, not the ones after. */
  onFile: boolean;
}

export interface RescueProgramSummary {
  program: RescueProgram;
  enrolled: boolean;
  /** The jurisdiction the account page offers an uploader for, from the saved shipping address. */
  primaryShipToState: string | null;
  ordersBlocked: boolean;
}

const requiredDocumentFields = gql`
  fragment RescueRequiredDocumentFields on RescueRequiredDocument {
    documentType
    state
    issuingAgency
    bucket
    satisfied
    onFile
    document {
      ...Rescue501c3Fields
    }
  }
  ${rescueDocumentFields}
`;

/** Everything the account page shows: what each program wants, what is filed, and any advisories. */
export const rescueDocumentOverviewQuery = gql`
  query rescueDocumentOverview {
    rescueDocumentOverview {
      programs {
        program
        enrolled
        primaryShipToState
        ordersBlocked
      }
      documents {
        ...RescueRequiredDocumentFields
      }
      notices {
        kind
        remedy
      }
    }
  }
  ${requiredDocumentFields}
`;

export interface RescueDocumentOverviewData {
  rescueDocumentOverview: {
    programs: RescueProgramSummary[];
    documents: RescueRequiredDocument[];
    notices: RescueExemptionNotice[];
  };
}

/**
 * What one order needs, keyed on where it ships rather than where the rescue sits — a certificate
 * answers a single jurisdiction, so shipping somewhere new asks for that state's document.
 *
 * `canOrder` is the gate the mutation enforces, not a hint. `blockedBy` names the documents holding
 * it; `notices` means the jurisdiction itself refuses it and no upload will help.
 */
export const rescueOrderDocumentRequirementsQuery = gql`
  query rescueOrderDocumentRequirements($input: RescueOrderDocumentRequirementsInput!) {
    rescueOrderDocumentRequirements(input: $input) {
      program
      shipToState
      complete
      canOrder
      blockedBy
      required {
        ...RescueRequiredDocumentFields
      }
      notices {
        kind
        remedy
      }
    }
  }
  ${requiredDocumentFields}
`;

export interface RescueOrderDocumentRequirements {
  program: RescueProgram;
  shipToState: string | null;
  complete: boolean;
  canOrder: boolean;
  blockedBy: RescueDocumentType[];
  required: RescueRequiredDocument[];
  notices: RescueExemptionNotice[];
}

export interface RescueOrderDocumentRequirementsData {
  rescueOrderDocumentRequirements: RescueOrderDocumentRequirements;
}

export interface RescueOrderDocumentRequirementsVariables {
  input: { program: RescueProgram; shipToState?: string | null };
}

/**
 * File a document by type and jurisdiction. Replaces `rescueSubmit501c3Document`, which can only
 * file a letter. The presigned URL is unchanged: it signs a location, and the type matters at
 * filing rather than at upload.
 */
export const rescueSubmitExemptionDocumentMutation = gql`
  mutation rescueSubmitExemptionDocument($input: RescueSubmitExemptionDocumentInput!) {
    rescueSubmitExemptionDocument(input: $input) {
      ...Rescue501c3Fields
    }
  }
  ${rescueDocumentFields}
`;

export interface RescueSubmitExemptionDocumentVariables {
  input: {
    uploadedFileKey: string;
    fileName: string;
    documentType: RescueDocumentType;
    /** Required on a state exemption document, ignored on the letter. */
    state?: string | null;
  };
}

export interface RescueSubmitExemptionDocumentData {
  rescueSubmitExemptionDocument: RescueDocument;
}
